import { useState } from 'react';
import axios, { AxiosError } from 'axios';
import api from '../utils/api';

interface ApiResponse<T = any> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

interface UseApiOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
}

interface ApiErrorResponse {
  message: string;
}

export const useApi = (options: UseApiOptions = {}) => {
  const [state, setState] = useState<ApiResponse>({
    data: null,
    error: null,
    loading: false,
  });

  const request = async <T>(
    method: string,
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const response = await api({
        method,
        url: endpoint,
        data,
        headers: {
          ...headers,
        },
      });

      setState(prev => ({
        ...prev,
        data: response.data,
        loading: false,
        error: null,
      }));

      options.onSuccess?.(response.data);

      return {
        data: response.data,
        error: null,
        loading: false,
      };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      const errorMessage = 
        error.response?.data?.message || 
        error.message || 
        'An error occurred';

      setState(prev => ({ 
        ...prev, 
        error: errorMessage, 
        loading: false 
      }));
      options.onError?.(errorMessage);

      return { data: null, error: errorMessage, loading: false };
    }
  };

  const get = <T>(endpoint: string, headers?: Record<string, string>) =>
    request<T>('GET', endpoint, undefined, headers);

  const post = <T>(endpoint: string, data?: any, headers?: Record<string, string>) =>
    request<T>('POST', endpoint, data, headers);

  const put = <T>(endpoint: string, data?: any, headers?: Record<string, string>) =>
    request<T>('PUT', endpoint, data, headers);

  const del = <T>(endpoint: string, headers?: Record<string, string>) =>
    request<T>('DELETE', endpoint, undefined, headers);

  return {
    ...state,
    get,
    post,
    put,
    delete: del,
  };
};

export default useApi;
